import React, { useState } from 'react';
import { X, Mail, Phone } from 'lucide-react';
import { Button, TextArea } from '../shared';
import { formatDate, formatTime } from '../../utils/dateUtils';

const ContactMessageDetailModal = ({
 message,
 onClose,
 INQUIRY_LABELS,
 updateMessage,
 replyMessage,
 isReplying,
 toast,
}) => {
 const [replyText, setReplyText] = useState('');

 const handleSendReply = () => {
 const trimmed = replyText.trim();
 if (!trimmed) {
 toast.error(null, 'Please enter a reply message');
 return;
 }
 replyMessage(
 { id: message.id, reply: trimmed },
 {
 onSuccess: () => {
 toast.success(null, 'Reply sent successfully');
 setReplyText('');
 onClose();
 },
 onError: (error) => {
 const msg = error?.response?.data?.message || 'Failed to send reply';
 toast.error(null, msg);
 },
 }
 );
 };

 const handleMarkReplied = () => {
 updateMessage({ id: message.id, data: { status: 'replied' } });
 toast.success(null, 'Marked as replied');
 onClose();
 };

 if (!message) return null;

 return (
 <div
 className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
 onClick={() => onClose()}
 >
 <div
 className="bg-surface rounded-xl shadow-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="p-6">
 <div className="flex justify-between items-start mb-6">
 <h2 className="text-xl font-bold text-gray-900">Contact Message</h2>
 <button
 onClick={() => onClose()}
 className="text-gray-400 hover:text-gray-600"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 <div className="space-y-4">
 <div>
 <p className="text-sm font-medium text-gray-500">From</p>
 <p className="text-base font-medium text-gray-900">{message.name}</p>
 <a
 href={`mailto:${message.email}`}
 className="text-ink hover:underline flex items-center gap-1"
 >
 <Mail className="w-4 h-4" />
 {message.email}
 </a>
 {message.phone && (
 <a
 href={`tel:${message.phone}`}
 className="text-ink hover:underline flex items-center gap-1 mt-1"
 >
 <Phone className="w-4 h-4" />
 {message.phone}
 </a>
 )}
 </div>
 <div>
 <p className="text-sm font-medium text-gray-500">Subject</p>
 <p className="text-base font-medium text-gray-900">{message.subject}</p>
 </div>
 <div>
 <p className="text-sm font-medium text-gray-500">Type</p>
 <p className="text-base text-gray-900">
 {INQUIRY_LABELS[message.inquiry_type] || message.inquiry_type}
 </p>
 </div>
 <div>
 <p className="text-sm font-medium text-gray-500">Date</p>
 <p className="text-base text-gray-900">
 {formatDate(message.created_at, 'long')} at {formatTime(message.created_at)}
 </p>
 </div>
 <div>
 <p className="text-sm font-medium text-gray-500 mb-2">Message</p>
 <div className="bg-surface-muted rounded-lg p-4 text-gray-900 whitespace-pre-wrap">
 {message.message}
 </div>
 </div>

 {/* Reply textarea */}
 <div>
 <TextArea
 label="Reply"
 placeholder="Type your reply here. It will be sent via email to the contact."
 value={replyText}
 onChange={(e) => setReplyText(e.target.value)}
 rows={5}
 className="mt-2"
 />
 </div>
 </div>

 <div className="mt-6 flex flex-wrap gap-3">
 <Button
 onClick={handleSendReply}
 disabled={isReplying || !replyText.trim()}
 >
 <Mail className="w-4 h-4 mr-2 inline" />
 {isReplying ? 'Sending...' : 'Send Reply'}
 </Button>
 <a
 href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject)}`}
 className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-surface-muted text-gray-700"
 >
 <Mail className="w-4 h-4" />
 Reply via Email
 </a>
 {message.status !== 'replied' && (
 <Button variant="outline" onClick={handleMarkReplied}>
 Mark as Replied
 </Button>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default ContactMessageDetailModal;
